// src/components/ResearchCalendarPage.js
//
// Read/write view of the shared Research Google Calendar, rendered inside RegX.
// This is purely additive: it reads via the new `list` action added to
// appsscript/research-calendar.gs and writes via the SAME upsert/delete path
// PatientDetailPage's ScheduleVisitModal already uses
// (src/services/researchCalendarService.js) — nothing about that existing flow
// is touched. Google Calendar-style weekly grid, room-colored events, and a
// coordinator double-booking check so coordinators can see at a glance which
// room is free and who's already tied up.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
  CalendarRange,
  AlertTriangle,
  Loader2,
  RefreshCw,
  MapPin,
  Users,
  Undo2,
  StickyNote,
  Info,
} from "lucide-react";
import {
  listCalendarEvents,
  upsertCalendarEvent,
  deleteCalendarEvent,
} from "../services/researchCalendarService";
import { fetchActiveCoordinators } from "../services/coordinatorService";
import SearchableSelect from "./SearchableSelect";
import { useCalendarRooms } from "./researchCalendarRooms";
import CalendarEventModal from "./CalendarEventModal";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOUR_START = 6; // 6 AM
const HOUR_END = 20; // 8 PM
const PX_PER_HOUR = 64;
const PX_PER_MIN = PX_PER_HOUR / 60;

const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const minutesSinceMidnight = (d) => d.getHours() * 60 + d.getMinutes();
const fmtTime = (d) =>
  d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtHourLabel = (h) => {
  const period = h < 12 || h === 24 ? "AM" : "PM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12} ${period}`;
};
const toDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const toTimeInput = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

// Groups overlapping events into side-by-side columns (Google Calendar style)
// so simultaneous bookings in the same room/day are still both visible.
function layoutDay(dayEvents) {
  const sorted = [...dayEvents].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin
  );
  const clusters = [];
  let current = [];
  let currentEnd = -Infinity;
  for (const ev of sorted) {
    if (current.length === 0 || ev.startMin < currentEnd) {
      current.push(ev);
      currentEnd = Math.max(currentEnd, ev.endMin);
    } else {
      clusters.push(current);
      current = [ev];
      currentEnd = ev.endMin;
    }
  }
  if (current.length) clusters.push(current);

  const positioned = [];
  for (const cluster of clusters) {
    const colEnds = [];
    for (const ev of cluster) {
      let col = colEnds.findIndex((end) => ev.startMin >= end);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(ev.endMin);
      } else {
        colEnds[col] = ev.endMin;
      }
      positioned.push({ ...ev, col, totalCols: 0 });
    }
    const totalCols = colEnds.length;
    for (let i = positioned.length - cluster.length; i < positioned.length; i++) {
      positioned[i].totalCols = totalCols;
    }
  }
  return positioned;
}

export default function ResearchCalendarPage() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [coordinators, setCoordinators] = useState([]);
  const [coordinatorFilter, setCoordinatorFilter] = useState("All");
  // This site's rooms (falls back to the defaults until the config resolves).
  const {
    rooms: ROOMS,
    roomFill,
    roomAccent,
    roomLabel,
    loaded: roomsLoaded,
    connected: calendarConnected,
  } = useCalendarRooms();
  // Only claim "not set up" once we've actually resolved this site's config.
  const notConnected = roomsLoaded && !calendarConnected;
  // null = "no filter, show every room". A non-empty Set is a SHOW-list: only
  // the rooms in it are drawn. Deliberately NOT seeded from ROOMS: the room
  // list arrives asynchronously, and a Set built from the defaults would hide
  // a site's own rooms the moment its config loaded.
  const [activeRooms, setActiveRooms] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEvent, setModalEvent] = useState({});
  // Session-only undo stack for changes made from this page (create/edit/remove).
  // Doesn't touch PatientDetailPage's ScheduleVisitModal flow at all.
  const [undoStack, setUndoStack] = useState([]);
  const [undoing, setUndoing] = useState(false);
  const [undoMessage, setUndoMessage] = useState("");

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const weekEnd = weekDays[6];
  const today = new Date();

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const timeMin = weekStart.toISOString();
      const timeMax = addDays(weekStart, 7).toISOString();
      const raw = await listCalendarEvents(timeMin, timeMax);
      const parsed = raw
        .filter((e) => e.start && e.end)
        .map((e) => ({ ...e, startDate: new Date(e.start), endDate: new Date(e.end) }))
        .filter((e) => !isNaN(e.startDate) && !isNaN(e.endDate));
      setEvents(parsed);
    } catch (e) {
      setError(e.message || "Couldn't load the Research calendar.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const pushUndo = (entry) => {
    setUndoStack((s) => [...s, entry].slice(-10));
  };

  const eventFieldsFrom = (fields) => ({
    date: fields.date,
    startTime: fields.startTime,
    endTime: fields.endTime,
    room: fields.room,
    otherRoom: fields.otherRoom,
    coordinatorName: fields.coordinator,
    coordinatorEmail: fields.coordinatorEmail,
    study: fields.study,
    visit: fields.visit,
    notes: fields.notes,
    patientName: fields.patientName,
    patientToken: fields.patientToken,
    createdBy: fields.createdBy,
  });

  const handleUndo = async () => {
    const entry = undoStack[undoStack.length - 1];
    if (!entry || undoing) return;
    setUndoing(true);
    setError("");
    try {
      if (entry.type === "create") {
        await deleteCalendarEvent(entry.eventId);
      } else if (entry.type === "update") {
        await upsertCalendarEvent({ eventId: entry.eventId, ...eventFieldsFrom(entry.before) });
      } else if (entry.type === "delete") {
        await upsertCalendarEvent(eventFieldsFrom(entry.before));
      }
      setUndoStack((s) => s.slice(0, -1));
      setUndoMessage(`Undid: ${entry.label}`);
      setTimeout(() => setUndoMessage(""), 4000);
      await loadEvents();
    } catch (e) {
      setError(e.message || "Couldn't undo that change.");
    } finally {
      setUndoing(false);
    }
  };

  useEffect(() => {
    fetchActiveCoordinators()
      .then(setCoordinators)
      .catch(() => setCoordinators([]));
  }, []);

  // Same coordinator, overlapping time anywhere in the visible week = a
  // physically-impossible double-booking. Flag both events involved.
  const conflictIds = useMemo(() => {
    const byCoordinator = {};
    for (const ev of events) {
      if (!ev.coordinatorName) continue;
      (byCoordinator[ev.coordinatorName] ||= []).push(ev);
    }
    const conflicts = new Set();
    Object.values(byCoordinator).forEach((list) => {
      const sorted = [...list].sort((a, b) => a.startDate - b.startDate);
      for (let i = 0; i < sorted.length; i++) {
        for (let j = i + 1; j < sorted.length; j++) {
          if (sorted[j].startDate < sorted[i].endDate) {
            conflicts.add(sorted[i].id);
            conflicts.add(sorted[j].id);
          } else {
            break;
          }
        }
      }
    });
    return conflicts;
  }, [events]);

  const filteringRooms = !!activeRooms && activeRooms.size > 0;

  const visibleEvents = events.filter((e) => {
    if (coordinatorFilter !== "All" && e.coordinatorName !== coordinatorFilter) return false;
    const room = e.room || "Other";
    if (activeRooms && !activeRooms.has(room)) return false;
    return true;
  });

  const dayColumns = weekDays.map((day) => {
    const dayEvents = visibleEvents
      .filter((e) => sameDay(e.startDate, day))
      .map((e) => ({
        ...e,
        startMin: Math.max(minutesSinceMidnight(e.startDate), HOUR_START * 60),
        endMin: Math.min(
          Math.max(minutesSinceMidnight(e.endDate), minutesSinceMidnight(e.startDate) + 15),
          HOUR_END * 60
        ),
      }));
    return { day, events: layoutDay(dayEvents) };
  });

  // Select-to-show, multi-select. prev === null means "no filter, everything is
  // shown", so the first click narrows down to just that room rather than
  // hiding it; further clicks add/remove rooms from the shown set. Deselecting
  // the last one clears the filter instead of leaving an empty grid.
  const toggleRoom = (name) => {
    setActiveRooms((prev) => {
      if (!prev) return new Set([name]);
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next.size === 0 ? null : next;
    });
  };

  const openNewEvent = (prefill = {}) => {
    // Booking would be rejected anyway (client guard + server membership/provider
    // checks), but opening a form that can only fail is a worse way to say no.
    if (notConnected) return;
    setModalEvent(prefill);
    setModalOpen(true);
  };

  const openEditEvent = (ev) => {
    setModalEvent({
      eventId: ev.id,
      patientName: ev.patientName,
      patientToken: ev.patientToken,
      date: toDateInput(ev.startDate),
      startTime: toTimeInput(ev.startDate),
      endTime: toTimeInput(ev.endDate),
      room: ev.room || "Other",
      otherRoom: ev.otherRoom,
      coordinator: ev.coordinatorName,
      coordinatorEmail: ev.coordinatorEmail,
      study: ev.study,
      visit: ev.visit,
      notes: ev.notes,
      createdBy: ev.createdBy,
      eventLink: ev.htmlLink,
    });
    setModalOpen(true);
  };

  const handleGridClick = (day, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let mins = HOUR_START * 60 + Math.round(y / PX_PER_MIN / 15) * 15;
    mins = Math.min(Math.max(mins, HOUR_START * 60), HOUR_END * 60 - 30);
    const startD = new Date(day);
    startD.setHours(0, mins, 0, 0);
    const endD = new Date(startD.getTime() + 30 * 60000);
    openNewEvent({ date: toDateInput(day), startTime: toTimeInput(startD), endTime: toTimeInput(endD) });
  };

  const hourMarks = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  const gridHeight = (HOUR_END - HOUR_START) * PX_PER_HOUR;

  const rangeLabel = (() => {
    const opts = { month: "short", day: "numeric" };
    const startStr = weekStart.toLocaleDateString([], opts);
    const endStr = weekEnd.toLocaleDateString([], { ...opts, year: "numeric" });
    return `${startStr} – ${endStr}`;
  })();

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative px-3 sm:px-4 lg:px-6 py-4 max-w-[1400px] mx-auto space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                Research Calendar
              </div>
              <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white leading-tight truncate flex items-center gap-2">
                <CalendarRange className="w-4 h-4 text-purple-300" />
                Room & Coordinator Schedule
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0 || undoing}
              title={
                undoStack.length > 0
                  ? `Undo ${undoStack[undoStack.length - 1].label}`
                  : "Nothing to undo"
              }
              className="inline-flex items-center gap-1.5 p-2 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-slate-300 hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/[0.04] disabled:hover:border-white/10"
            >
              {undoing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Undo2 className="w-3.5 h-3.5" />
              )}
            </button>
            <button
              onClick={loadEvents}
              title="Refresh"
              className="p-2 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-slate-300 hover:text-white transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => openNewEvent({ date: toDateInput(today) })}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-purple-500 to-blue-500 shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)] hover:brightness-110 transition"
            >
              <Plus className="w-3.5 h-3.5" />
              New Event
            </button>
          </div>
        </div>

        {/* Controls row: week nav + filters */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setWeekStart((w) => addDays(w, -7))}
              className="p-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] text-slate-300 hover:text-white transition"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setWeekStart(startOfWeek(new Date()))}
              className="px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] text-[11px] font-medium text-slate-300 hover:text-white transition"
            >
              Today
            </button>
            <button
              onClick={() => setWeekStart((w) => addDays(w, 7))}
              className="p-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] text-slate-300 hover:text-white transition"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="ml-2 text-sm font-semibold text-white">{rangeLabel}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Room legend / filter */}
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              {ROOMS.map((r) => {
                const picked = !!activeRooms && activeRooms.has(r.name);
                return (
                  <button
                    key={r.name}
                    onClick={() => toggleRoom(r.name)}
                    aria-pressed={picked}
                    title={
                      picked
                        ? `Stop showing only ${r.name}`
                        : filteringRooms
                          ? `Also show ${r.name}`
                          : `Show only ${r.name}`
                    }
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[11px] font-medium transition ${
                      picked
                        ? "border-purple-300/60 text-white bg-white/[0.12] ring-1 ring-purple-400/40"
                        : filteringRooms
                          ? "border-white/5 text-slate-500 bg-transparent opacity-50"
                          : "border-white/15 text-slate-100 bg-white/[0.05]"
                    }`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{
                        backgroundColor: r.fill,
                        boxShadow: `inset 0 0 0 1.5px ${r.accent}`,
                      }}
                    />
                    {r.name}
                  </button>
                );
              })}
              {filteringRooms && (
                <button
                  onClick={() => setActiveRooms(null)}
                  title="Show every room again"
                  className="px-2 py-1 rounded-lg border border-white/10 text-[11px] font-medium text-slate-300 hover:text-white hover:bg-white/[0.08] transition"
                >
                  All rooms
                </button>
              )}
            </div>

            {/* Coordinator filter */}
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-slate-400" />
              <div className="relative w-44">
                {/* Type to filter, then pick. "All" is the same value as before. */}
                <SearchableSelect
                  compact
                  value={coordinatorFilter}
                  options={coordinators.map((c) => c.name)}
                  onChange={setCoordinatorFilter}
                  clearOption={{ value: "All", label: "All coordinators" }}
                  placeholder="All coordinators"
                  noMatchText="No coordinator matches that."
                  inputClassName="w-full px-2.5 py-1 pr-7 rounded-lg bg-white/[0.04] border border-white/10 text-[11px] text-slate-200 placeholder:text-slate-400 hover:bg-white/[0.08] focus:outline-none focus:border-purple-400/50 transition"
                />
              </div>
            </div>
          </div>

          {/* How the room filter works — small, out of the way, but there the
              first time someone wonders why a click changed what they see. */}
          <div className="w-full flex items-center gap-1.5 text-[10px] text-slate-500 pt-0.5">
            <Info className="w-3 h-3 shrink-0" />
            {filteringRooms ? (
              <span>
                Showing{" "}
                <span className="text-slate-300">
                  {ROOMS.filter((r) => activeRooms.has(r.name))
                    .map((r) => r.name)
                    .join(", ") || "selected rooms"}
                </span>
                . Click another room to add it, click a highlighted one to drop
                it, or “All rooms” to clear the filter.
              </span>
            ) : (
              <span>
                Showing every room. Click a room to see only its bookings — click
                more than one to compare rooms side by side.
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-xl border border-rose-400/20 bg-rose-500/10 px-3 py-2.5 text-xs text-rose-200">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {conflictIds.size > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            A coordinator is booked on two overlapping visits this week — flagged events are outlined in red below.
          </div>
        )}

        {undoMessage && (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-400/20 bg-emerald-500/10 px-3 py-2.5 text-xs text-emerald-200">
            <Undo2 className="w-3.5 h-3.5 shrink-0" />
            {undoMessage}
          </div>
        )}

        {/* Calendar grid */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
          {loading && (
            <div className="absolute inset-0 z-30 flex items-center justify-center gap-2.5 bg-[#0b0820]/70 backdrop-blur-sm text-sm text-slate-200">
              <Loader2 className="w-5 h-5 animate-spin text-purple-300" />
              Loading calendar…
            </div>
          )}
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Day headers */}
              <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-white/10 sticky top-0 z-10 bg-[#0b0820]/80 backdrop-blur-xl">
                <div />
                {weekDays.map((day) => {
                  const isToday = sameDay(day, today);
                  return (
                    <div
                      key={day.toISOString()}
                      className={`px-2 py-2.5 text-center border-l border-white/5 ${
                        isToday ? "bg-purple-500/10" : ""
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">
                        {DAY_LABELS[day.getDay()]}
                      </div>
                      <div
                        className={`text-sm font-semibold ${
                          isToday
                            ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 text-white"
                            : "text-slate-200"
                        }`}
                      >
                        {day.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Time grid */}
              <div className="relative grid grid-cols-[56px_repeat(7,1fr)]">
                {/* Hour gutter */}
                <div className="relative" style={{ height: gridHeight }}>
                  {hourMarks.map((h) => (
                    <div
                      key={h}
                      className="absolute right-2 -translate-y-1/2 text-[10px] text-slate-500"
                      style={{ top: (h - HOUR_START) * PX_PER_HOUR }}
                    >
                      {fmtHourLabel(h)}
                    </div>
                  ))}
                </div>

                {dayColumns.map(({ day, events: dayEvents }) => (
                  <div
                    key={day.toISOString()}
                    className="relative border-l border-white/5 cursor-crosshair"
                    style={{ height: gridHeight }}
                    onClick={(e) => handleGridClick(day, e)}
                  >
                    {hourMarks.map((h) => (
                      <div
                        key={h}
                        className="absolute left-0 right-0 border-t border-white/5"
                        style={{ top: (h - HOUR_START) * PX_PER_HOUR }}
                      />
                    ))}
                    {sameDay(day, today) && (
                      <div
                        className="absolute left-0 right-0 z-30 pointer-events-none"
                        style={{
                          top: Math.min(
                            Math.max(minutesSinceMidnight(today) - HOUR_START * 60, 0),
                            (HOUR_END - HOUR_START) * 60
                          ) * PX_PER_MIN,
                        }}
                      >
                        <div className="absolute inset-x-0 top-0 border-t-2 border-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)]" />
                        <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.9)]" />
                        <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.9)]" />
                      </div>
                    )}

                    {dayEvents.map((ev) => {
                      const isConflict = conflictIds.has(ev.id);
                      const fill = roomFill(ev.room);
                      const accent = roomAccent(ev.room);
                      const widthPct = 100 / ev.totalCols;
                      const leftPct = ev.col * widthPct;
                      const tooltipBits = [
                        ev.patientName || "Visit",
                        roomLabel(ev.room, ev.otherRoom),
                        `${fmtTime(ev.startDate)}–${fmtTime(ev.endDate)}`,
                        ev.coordinatorName || null,
                        ev.notes ? `Notes: ${ev.notes}` : null,
                        ev.createdBy ? `Created by ${ev.createdBy.split("@")[0]}` : null,
                      ].filter(Boolean);
                      return (
                        <button
                          key={ev.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditEvent(ev);
                          }}
                          title={tooltipBits.join(" — ")}
                          className={`absolute rounded-lg px-2 py-1 text-left overflow-hidden text-[11px] leading-tight shadow-sm hover:brightness-95 hover:z-20 transition ${
                            isConflict ? "ring-2 ring-rose-600 z-10" : ""
                          }`}
                          style={{
                            top: (ev.startMin - HOUR_START * 60) * PX_PER_MIN,
                            height: Math.max((ev.endMin - ev.startMin) * PX_PER_MIN, 20) - 2,
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                            backgroundColor: fill,
                            borderLeft: `4px solid ${accent}`,
                            // Text uses the room's own deep accent shade (tinted-badge style —
                            // same hue as the fill, just darker) instead of plain black. Reads
                            // much easier than flat dark ink dropped on a colored background,
                            // and — since it's inline, not a Tailwind text-* class — stays put
                            // in both site themes instead of drifting like the app's translucent
                            // dark-glass surfaces do.
                            color: accent,
                          }}
                        >
                          <div className="flex items-center gap-1 font-semibold truncate">
                            {isConflict && (
                              <AlertTriangle
                                className="w-2.5 h-2.5 shrink-0"
                                style={{ color: "#BE123C" }}
                              />
                            )}
                            {ev.patientName || "Visit"}
                            {ev.notes && (
                              <StickyNote
                                className="w-2.5 h-2.5 shrink-0"
                                style={{ color: accent, opacity: 0.7 }}
                              />
                            )}
                          </div>
                          <div className="truncate" style={{ color: accent, opacity: 0.85 }}>
                            {fmtTime(ev.startDate)}–{fmtTime(ev.endDate)}
                          </div>
                          {ev.coordinatorName && (
                            <div className="truncate" style={{ color: accent, opacity: 0.85 }}>
                              {ev.coordinatorName}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {!loading && events.length === 0 && !error && (
            <div className="py-10 text-center text-sm text-slate-400">
              {notConnected ? (
                <>
                  <CalendarRange className="w-6 h-6 mx-auto mb-2 text-slate-500" />
                  <p className="text-slate-300 font-medium">
                    No calendar is connected for this site.
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    This site isn't linked to a Google Calendar yet, so there's
                    nothing to show. An admin can set one up in Manage → Calendar.
                  </p>
                </>
              ) : (
                "No visits scheduled this week."
              )}
            </div>
          )}
        </div>
      </div>

      <CalendarEventModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        event={modalEvent}
        // Everything already on the calendar for the week in view — the modal
        // uses it only to warn about a room that's already taken. Room filters
        // deliberately don't apply here: a hidden room can still be booked.
        existingEvents={events}
        onSaved={(saved) => {
          const wasCreate = !modalEvent.eventId;
          pushUndo(
            wasCreate
              ? {
                  type: "create",
                  eventId: saved.eventId,
                  label: `added ${saved.patientName || "visit"}`,
                }
              : {
                  type: "update",
                  eventId: saved.eventId,
                  before: { ...modalEvent },
                  label: `edit to ${saved.patientName || "visit"}`,
                }
          );
          loadEvents();
        }}
        onRemoved={() => {
          pushUndo({
            type: "delete",
            before: { ...modalEvent },
            label: `removal of ${modalEvent.patientName || "visit"}`,
          });
          loadEvents();
        }}
      />
    </div>
  );
}
